/**
 * Client-side permission matrix mirroring the backend `requireRole(...)`
 * guards in backend/src/routes/erp/*.ts. The server is always the source
 * of truth; this map only decides which actions/nav the UI advertises so
 * roles are not shown buttons that would 403.
 */
import { useAuth } from '@/contexts/AuthContext';

export type Role =
  | 'Owner'
  | 'Admin'
  | 'Finance'
  | 'Sales'
  | 'Engineer'
  | 'Designer'
  | 'Viewer';

export const PERMISSIONS = {
  // invoices.ts
  'invoices.read': ['Owner', 'Admin', 'Finance', 'Sales', 'Viewer'],
  'invoices.write': ['Owner', 'Admin', 'Finance', 'Sales'],
  'invoices.delete': ['Owner', 'Admin', 'Finance'],
  'invoices.pay': ['Owner', 'Admin', 'Finance'],
  'invoices.void': ['Owner', 'Admin', 'Finance'],
  // bills.ts
  'bills.read': ['Owner', 'Admin', 'Finance', 'Viewer'],
  'bills.write': ['Owner', 'Admin', 'Finance'],
  // expenses.ts
  'expenses.read': ['Owner', 'Admin', 'Finance', 'Viewer'],
  'expenses.write': ['Owner', 'Admin', 'Finance', 'Sales', 'Engineer', 'Designer'],
  'expenses.approve': ['Owner', 'Admin', 'Finance'],
  // contracts.ts
  'contracts.read': ['Owner', 'Admin', 'Finance', 'Sales', 'Viewer'],
  'contracts.write': ['Owner', 'Admin', 'Sales'],
  'contracts.terminate': ['Owner', 'Admin'],
  // quotations.ts
  'quotations.read': ['Owner', 'Admin', 'Finance', 'Sales', 'Viewer'],
  'quotations.write': ['Owner', 'Admin', 'Sales'],
  'quotations.convert': ['Owner', 'Admin', 'Finance', 'Sales'],
  // customers.ts
  'customers.read': ['Owner', 'Admin', 'Finance', 'Sales', 'Viewer'],
  'customers.write': ['Owner', 'Admin', 'Finance', 'Sales'],
  'customers.manage': ['Owner', 'Admin', 'Finance'],
  // vendors.ts
  'vendors.read': ['Owner', 'Admin', 'Finance'],
  'vendors.write': ['Owner', 'Admin', 'Finance'],
  // employees.ts
  'employees.read': ['Owner', 'Admin', 'Finance', 'Viewer'],
  'employees.write': ['Owner', 'Admin'],
  // payroll.ts
  'payroll.read': ['Owner', 'Admin', 'Finance'],
  'payroll.write': ['Owner', 'Admin', 'Finance'],
  // subscriptions.ts
  'subscriptions.read': ['Owner', 'Admin', 'Finance', 'Viewer'],
  'subscriptions.write': ['Owner', 'Admin', 'Finance'],
  // payments.ts
  'payments.read': ['Owner', 'Admin', 'Finance', 'Viewer'],
  // items.ts — the catalogue is NOT gated on the inventory module, so Sales can
  // write it: somebody adding "the new 500g bag" while on the phone is the point.
  'items.read': ['Owner', 'Admin', 'Finance', 'Sales', 'Viewer'],
  'items.write': ['Owner', 'Admin', 'Finance', 'Sales'],
  'items.delete': ['Owner', 'Admin', 'Finance'],
  // inventory.ts — stock is the ledger side. Sales may not post an adjustment,
  // because that is a journal entry, the same line expenses.approve draws.
  'stock.read': ['Owner', 'Admin', 'Finance', 'Sales', 'Viewer'],
  'stock.write': ['Owner', 'Admin', 'Finance'],
  'stock.post': ['Owner', 'Admin', 'Finance'],
  // reports.ts
  'reports.read': ['Owner', 'Admin', 'Finance', 'Viewer'],
  'reports.pdf': ['Owner', 'Admin', 'Finance'],
  // settings.ts
  'settings.write': ['Owner', 'Admin'],
  // users.ts
  'users.manage': ['Owner'],
} as const satisfies Record<string, readonly Role[]>;

export type Capability = keyof typeof PERMISSIONS;

export function can(role: string | null | undefined, capability: Capability): boolean {
  if (!role) return false;
  return (PERMISSIONS[capability] as readonly string[]).includes(role);
}

export function useCan(): (capability: Capability) => boolean {
  const { user } = useAuth();
  return (capability) => can(user?.role, capability);
}
